/**
 * TypeScript mirror of `GET /api/templates`, `GET /api/templates/{id}`, and `GET /api/classes`
 * (WP3 contract), plus a couple of pure grouping helpers over a `Template`. Query options follow
 * the same shape as `pingQuery` (`lib/api.ts`) / `meQuery` (`lib/auth.ts`) — no React here either.
 */
import { apiGet } from './api';
import type { AppliesTo, FieldConfig, FieldType } from './fieldValue';

export interface TemplateSummary {
  id: number;
  key: string;
  version: number;
  name: string;
  seriesLabel: string;
  published: boolean;
}

export interface Section {
  id: number;
  key: string;
  name: string;
  sortOrder: number;
  isChapter: boolean;
}

/**
 * `overlayGroup`/`exportColumn` are `null` for ROOM-scoped fields (e.g. `keyboard_layout`), which
 * have no per-row overlay presence or export column.
 */
export interface Field {
  id: number;
  sectionId: number;
  key: string;
  label: string;
  sortOrder: number;
  type: FieldType;
  appliesTo: AppliesTo;
  allowFootnote: boolean;
  overlayGroup: string | null;
  exportColumn: string | null;
  config: FieldConfig;
}

export interface Template extends TemplateSummary {
  sections: Section[];
  fields: Field[];
}

export interface GameClass {
  id: number;
  slug: string;
  name: string;
  archetype: string;
  faction: string;
  sortOrder: number;
  active: boolean;
}

export const templatesQuery = {
  queryKey: ['templates'] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) => apiGet<TemplateSummary[]>('/api/templates', signal),
};

export function templateQuery(id: number) {
  return {
    queryKey: ['templates', id] as const,
    queryFn: ({ signal }: { signal: AbortSignal }) => apiGet<Template>(`/api/templates/${id}`, signal),
  };
}

export const classesQuery = {
  queryKey: ['classes'] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) => apiGet<GameClass[]>('/api/classes', signal),
};

export interface SectionWithFields {
  section: Section;
  fields: Field[];
}

/**
 * Groups `template.fields` under their `template.sections`, both sorted by `sortOrder`. A section
 * with no fields still appears (with an empty `fields` array) so chapter navigation can render it.
 */
export function fieldsBySection(template: Template): SectionWithFields[] {
  const bySectionId = new Map<number, Field[]>();
  for (const field of template.fields) {
    const bucket = bySectionId.get(field.sectionId);
    if (bucket) {
      bucket.push(field);
    } else {
      bySectionId.set(field.sectionId, [field]);
    }
  }

  const sections = [...template.sections].sort((a, b) => a.sortOrder - b.sortOrder);
  return sections.map((section) => {
    const fields = [...(bySectionId.get(section.id) ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
    return { section, fields };
  });
}

/**
 * Distinct `overlayGroup` values across `template.fields`, in field order (`fields` is assumed
 * already sorted the way the API returns it — by `sortOrder`), first-occurrence order preserved.
 * Fields with no overlay group (`null`, e.g. ROOM-only fields) are skipped.
 *
 * Note: the "Overview" group (Class, Rated By, Video URL) shown on the OBS overlay is room data,
 * not a template field — it never appears in `template.fields`, so it never appears in this
 * function's output either. The overlay page must prepend "Overview" itself ahead of the
 * template-derived groups returned here.
 */
export function overlayGroups(template: Template): string[] {
  const seen = new Set<string>();
  const groups: string[] = [];
  for (const field of template.fields) {
    if (field.overlayGroup != null && !seen.has(field.overlayGroup)) {
      seen.add(field.overlayGroup);
      groups.push(field.overlayGroup);
    }
  }
  return groups;
}
